import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Home, Play, Pause, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';

const sounds = [
  { id: 'rain', name: 'Rain', icon: '🌧️', url: 'https://cdn.pixabay.com/audio/2022/03/10/audio_13c89b8e04.mp3', color: 'from-blue-500 to-cyan-500' },
  { id: 'ocean', name: 'Ocean', icon: '🌊', url: 'https://cdn.pixabay.com/audio/2022/06/07/audio_b7727e55e7.mp3', color: 'from-cyan-500 to-blue-500' },
  { id: 'forest', name: 'Forest', icon: '🌲', url: 'https://cdn.pixabay.com/audio/2022/03/15/audio_95aafef3e5.mp3', color: 'from-green-500 to-emerald-500' },
  { id: 'stream', name: 'Stream', icon: '💧', url: 'https://cdn.pixabay.com/audio/2022/03/15/audio_1f47b1002e.mp3', color: 'from-teal-500 to-cyan-500' },
  { id: 'thunder', name: 'Thunder', icon: '⛈️', url: 'https://cdn.pixabay.com/audio/2021/08/04/audio_0625c1539c.mp3', color: 'from-slate-500 to-blue-500' },
  { id: 'fire', name: 'Campfire', icon: '🔥', url: 'https://cdn.pixabay.com/audio/2022/03/12/audio_54c8743cff.mp3', color: 'from-orange-500 to-red-500' },
  { id: 'wind', name: 'Wind', icon: '🍃', url: 'https://cdn.pixabay.com/audio/2022/03/15/audio_2741ef3657.mp3', color: 'from-lime-500 to-green-500' },
  { id: 'night', name: 'Crickets', icon: '🌙', url: 'https://cdn.pixabay.com/audio/2022/06/08/audio_bdb2f755e6.mp3', color: 'from-indigo-500 to-purple-500' },
];

export default function SleepSounds() {
  const [playing, setPlaying] = useState<string | null>(null);
  const [volume, setVolume] = useState(50);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useSeoMeta({ title: 'Sleep Sounds - Zenstr' });

  useEffect(() => {
    return () => audioRef.current?.pause();
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume / 100;
  }, [volume]);

  const play = (sound: typeof sounds[0]) => {
    if (playing === sound.id) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(sound.url);
    audio.loop = true;
    audio.volume = volume / 100;
    audio.play();
    audioRef.current = audio;
    setPlaying(sound.id);
  };

  const stop = () => {
    audioRef.current?.pause();
    setPlaying(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🧘‍♀️</span>
            <span className="font-display font-semibold">Zenstr</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm"><Home className="w-4 h-4 mr-2" /> Dashboard</Button>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-6 py-16 max-w-6xl">
        <div className="text-center space-y-12 mb-16">
          <div className="space-y-2">
            <h1 className="text-5xl md:text-6xl font-display font-bold">Sleep Sounds</h1>
            <p className="text-xl text-muted-foreground">Peaceful ambience for relaxation</p>
          </div>

          {playing && (
            <Card className="p-6 max-w-md mx-auto bg-card/50">
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Volume2 className="w-4 h-4" />
                    <span>Volume</span>
                  </div>
                  <span>{volume}%</span>
                </div>
                <Slider value={[volume]} onValueChange={(v) => setVolume(v[0])} max={100} />
                <Button onClick={stop} variant="outline" className="w-full">
                  <Pause className="w-4 h-4 mr-2" /> Stop
                </Button>
              </div>
            </Card>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {sounds.map((sound, i) => (
            <Card
              key={sound.id}
              className={`cursor-pointer hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 enter ${playing === sound.id ? 'ring-2 ring-primary' : ''}`}
              style={{ animationDelay: `${i * 0.05}s` }}
              onClick={() => play(sound)}
            >
              <div className={`p-8 bg-gradient-to-br ${sound.color} rounded-t-xl flex items-center justify-center`}>
                <span className="text-6xl">{sound.icon}</span>
              </div>
              <div className="p-4 text-center bg-card/50">
                <h3 className="font-display font-semibold">{sound.name}</h3>
                {playing === sound.id && (
                  <div className="flex justify-center gap-1 mt-2">
                    {[0,1,2].map(i => (
                      <div key={i} className="w-1 h-4 bg-primary rounded animate-pulse" style={{ animationDelay: `${i * 0.15}s` }} />
                    ))}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
